/**
 * Le panier : garder le nom brodé et son supplément d'accord.
 *
 * ## Pourquoi ce fichier existe
 *
 * Le supplément de 6 $ voyage dans une LIGNE DE PANIER SÉPARÉE — sa propre
 * variante Shopify. Le nom, lui, est une propriété du vêtement. Deux lignes
 * indépendantes dans un panier Shopify ordinaire, donc deux façons de repartir
 * avec une broderie non payée :
 *
 *   1. l'élève SUPPRIME la ligne du supplément ; le nom reste sur le vêtement ;
 *   2. l'élève AUGMENTE la quantité du vêtement ; le supplément ne suit pas.
 *      Trois chandails, un seul 6 $.
 *
 * Le second ne demande aucune mauvaise intention : c'est le geste le plus
 * naturel du monde.
 *
 * ## Ce que ce script fait
 *
 * Il compte les vêtements portant un nom, compte les suppléments payés, et
 * ajuste la ligne du supplément — NOM PAR NOM, parce qu'un panier peut porter
 * « Léa » et « Max » à des quantités différentes.
 *
 * ## Quand il se réveille — le point délicat
 *
 * Au chargement de la page, ET après chaque modification du panier.
 *
 * ⚠️ Ce second déclencheur ne peut PAS reposer sur un événement du thème.
 * Vérifié sur le thème en ligne le 2026-09-10 : son `cart.js` redessine quatre
 * sections (`main-cart-items`, `main-cart-footer`, `cart-icon-bubble`,
 * `cart-live-region-text`) et ne diffuse AUCUN événement DOM. Un script qui
 * n'écouterait que « cart:updated » ne tournerait donc jamais après une
 * suppression — exactement le geste qu'on veut rattraper.
 *
 * On observe donc les appels réseau vers /cart/*.js. L'observation est PASSIVE :
 * la promesse d'origine est retournée telle quelle, le thème ne voit rien.
 *
 * ## Ce qu'il ne fait jamais
 *
 *   · il ne touche à AUCUNE ligne sans `_portail` ;
 *   · il ne supprime ni ne modifie jamais un vêtement ;
 *   · il n'agit pas en silence — un message dit ce qui a été ajusté et pourquoi.
 *
 * Ce dernier point n'est pas cosmétique : un montant qui remonte tout seul sans
 * explication ressemble à un bogue, et on nous écrit.
 *
 * ## Ce qu'il ne peut pas garantir
 *
 * Il tourne dans le navigateur. Sur Shopify Basic il n'existe aucune Function
 * pour refuser un panier au moment du paiement — c'est la limite du plan, pas
 * un raccourci. Le vrai filet est côté serveur : le webhook `orders/create`
 * compare broderies et suppléments et pose le tag « NOM-NON-PAYE » sur toute
 * commande où l'écart subsiste. Ce script réduit les cas ; le tag les attrape.
 */
(function () {
  'use strict';

  var PROP_PORTAIL = '_portail';
  var PROP_NOM = 'Nom sur la manche';
  var PROP_SUPPLEMENT = '_supplement';
  var VALEUR_SUPPLEMENT = 'nom-manche';
  var PROP_NOM_BRODE = 'Nom brodé';

  var racine = document.querySelector('[data-updn-panier-nom]');
  if (!racine) return;

  var varianteSupplement = racine.getAttribute('data-variante') || '';
  var zoneMessage = racine.querySelector('[data-updn-message]');

  /** Un seul passage à la fois. */
  var enCours = false;

  /**
   * Le garde-fou anti-boucle.
   *
   * Un ajustement recharge la page ; le script repart et devrait alors ne plus
   * rien trouver à faire. S'il trouve encore un écart — variante épuisée,
   * supplément refusé par Shopify — on rechargerait sans fin. Trois reprises
   * par session suffisent largement, et au-delà on affiche le message sans
   * recharger : un panier légèrement faux vaut mieux qu'une page qui clignote.
   *
   * Le compteur vit dans sessionStorage pour survivre au rechargement, et il
   * est remis à zéro dès qu'un passage trouve le panier en ordre.
   */
  var CLE_REPRISES = 'updn-panier-nom-reprises';
  var MAX_REPRISES = 3;

  function reprises() {
    try { return parseInt(window.sessionStorage.getItem(CLE_REPRISES) || '0', 10) || 0; }
    catch (e) { return 0; }
  }
  function noterReprise(n) {
    try { window.sessionStorage.setItem(CLE_REPRISES, String(n)); } catch (e) { /* navigation privée */ }
  }

  function proprietes(ligne) {
    return (ligne && ligne.properties) || {};
  }

  function quantite(ligne) {
    var n = Number(ligne && ligne.quantity);
    return isFinite(n) && n > 0 ? Math.trunc(n) : 0;
  }

  function texte(v) {
    return String(v == null ? '' : v).trim();
  }

  /**
   * L'état du panier, regroupé par nom brodé.
   *
   * Chaque entrée retient aussi le slug du portail d'origine : c'est celui-là
   * qu'on reposera sur une ligne ajoutée, pas un slug deviné ailleurs dans le
   * panier. Un panier peut mélanger deux portails.
   */
  function compter(panier) {
    var parNom = {};

    function entree(nom) {
      if (!parNom[nom]) {
        parNom[nom] = { nom: nom, demande: 0, paye: 0, portail: null, lignes: [] };
      }
      return parNom[nom];
    }

    (panier.items || []).forEach(function (l) {
      var p = proprietes(l);
      var portail = texte(p[PROP_PORTAIL]);
      // On ne touche à rien qui ne vienne pas d'un portail.
      if (!portail) return;

      if (texte(p[PROP_SUPPLEMENT]) === VALEUR_SUPPLEMENT) {
        var nomPaye = texte(p[PROP_NOM_BRODE]);
        if (!nomPaye) return;
        var s = entree(nomPaye);
        s.paye += quantite(l);
        // Plusieurs lignes peuvent porter le même nom (deux ajouts successifs
        // avec des propriétés légèrement différentes). On les garde toutes :
        // n'en ajuster qu'une laisserait l'autre en trop.
        s.lignes.push({ cle: l.key, quantite: quantite(l) });
        return;
      }

      var nom = texte(p[PROP_NOM]);
      if (!nom) return;
      var v = entree(nom);
      v.demande += quantite(l);
      if (!v.portail) v.portail = portail;
    });

    return Object.keys(parNom).map(function (n) { return parNom[n]; });
  }

  /** Ce qu'il faut ajouter, ce qu'il faut réduire. */
  function planifier(etat) {
    var aAjouter = [];
    var aReduire = [];

    etat.forEach(function (e) {
      if (e.demande > e.paye) {
        aAjouter.push({
          nom: e.nom,
          quantite: e.demande - e.paye,
          // Un supplément payé sans vêtement correspondant n'a pas de portail
          // connu ; dans ce cas il n'y a rien à ajouter de toute façon.
          portail: e.portail,
        });
        return;
      }

      // Trop de suppléments : on retire l'excédent, ligne par ligne, en
      // commençant par la dernière ajoutée.
      var excedent = e.paye - e.demande;
      for (var i = e.lignes.length - 1; i >= 0 && excedent > 0; i--) {
        var l = e.lignes[i];
        var retire = Math.min(excedent, l.quantite);
        aReduire.push({ cle: l.cle, vers: l.quantite - retire });
        excedent -= retire;
      }
    });

    // Sans portail connu, on ne peut pas étiqueter la ligne ajoutée.
    aAjouter = aAjouter.filter(function (a) { return a.portail; });

    return { aAjouter: aAjouter, aReduire: aReduire };
  }

  function message(txt) {
    if (!zoneMessage) return;
    zoneMessage.textContent = txt;
    zoneMessage.hidden = false;
  }

  /**
   * Le `fetch` d'origine, capturé AVANT qu'on installe l'observateur.
   *
   * Nos propres appels passent par lui : s'observer soi-même n'apprendrait
   * rien et embrouillerait le compteur de reprises.
   */
  var fetchOriginal = window.fetch ? window.fetch.bind(window) : null;

  function chemin(suffixe) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + suffixe;
  }

  function lirePanier() {
    return fetchOriginal(chemin('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      });
  }

  function ajouter(entrees) {
    if (!entrees.length) return Promise.resolve();
    return fetchOriginal(chemin('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: entrees.map(function (e) {
          var p = {};
          p[PROP_PORTAIL] = e.portail;
          p[PROP_SUPPLEMENT] = VALEUR_SUPPLEMENT;
          p[PROP_NOM_BRODE] = e.nom;
          return { id: varianteSupplement, quantity: e.quantite, properties: p };
        }),
      }),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/add.js ' + r.status);
      return r.json();
    });
  }

  function reduire(entrees) {
    // Une à la fois : /cart/change.js ne traite qu'une ligne par appel, et
    // chaque appel renvoie un panier dont les clés restent valides.
    return entrees.reduce(function (chaine, e) {
      return chaine.then(function () {
        return fetchOriginal(chemin('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: e.cle, quantity: e.vers }),
        }).then(function (r) {
          if (!r.ok) throw new Error('cart/change.js ' + r.status);
        });
      });
    }, Promise.resolve());
  }

  function annoncer(plan) {
    var ajoutes = plan.aAjouter.reduce(function (s, e) { return s + e.quantite; }, 0);
    if (ajoutes > 0) {
      message('Le nom brodé sur la manche est un supplément. '
        + (ajoutes > 1
            ? ajoutes + ' suppléments ont été ajoutés'
            : 'Un supplément a été ajouté')
        + ' pour correspondre aux vêtements à broder. '
        + 'Pour ne pas le payer, retirez le nom de l’article dans le portail.');
    } else {
      message('Le supplément « nom sur la manche » a été ajusté : '
        + 'plus aucun vêtement du panier ne porte ce nom.');
    }
  }

  function reconcilier() {
    if (enCours || !fetchOriginal) return Promise.resolve();
    enCours = true;

    return lirePanier()
      .then(function (panier) {
        var plan = planifier(compter(panier));
        if (!plan.aAjouter.length && !plan.aReduire.length) return null;

        /**
         * Sans variante de supplément configurée, on ne peut RIEN ajouter.
         * Plutôt que d'échouer en silence, on le dit : c'est un réglage de
         * section manquant, et personne ne le devinerait depuis le panier.
         */
        if (plan.aAjouter.length && !varianteSupplement) {
          message('Le supplément « nom sur la manche » n’est pas configuré. '
            + 'Communiquez avec nous avant de payer.');
          return null;
        }

        return reduire(plan.aReduire)
          .then(function () { return ajouter(plan.aAjouter); })
          .then(function () { return plan; });
      })
      .then(function (plan) {
        enCours = false;
        if (!plan) {
          // Panier en ordre : le compteur repart de zéro.
          noterReprise(0);
          return;
        }

        annoncer(plan);

        /**
         * Il FAUT redessiner : le thème ne redessine que sur ses propres appels,
         * pas sur les nôtres. Un total resté faux à l'écran serait pire que
         * l'attente — l'élève paierait en croyant devoir moins.
         *
         * Au-delà du seuil, on garde le message et on renonce au rechargement
         * plutôt que de faire clignoter la page indéfiniment.
         */
        var n = reprises() + 1;
        noterReprise(n);
        if (n > MAX_REPRISES) return;

        window.setTimeout(function () { window.location.reload(); }, 1500);
      })
      .catch(function () {
        enCours = false;
        // Le panier reste tel qu'il était — jamais à moitié ajusté, puisque la
        // réduction précède l'ajout et qu'un échec d'ajout laisse simplement un
        // écart. Le tag serveur prendra le relais.
      });
  }

  /**
   * L'observateur des appels au panier.
   *
   * PASSIF : on retourne la promesse d'origine intacte et on se contente de lui
   * accrocher une suite. Le thème ne voit aucune différence, et une exception de
   * notre côté ne peut pas casser son propre traitement.
   *
   * Le petit délai laisse le thème finir de redessiner ses sections avant qu'on
   * relise le panier — sinon on lirait un état qu'il est en train d'écraser.
   */
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var minuterie = null;

  function planifierReconciliation() {
    if (minuterie) window.clearTimeout(minuterie);
    minuterie = window.setTimeout(function () { minuterie = null; reconcilier(); }, 250);
  }

  if (fetchOriginal && window.fetch) {
    window.fetch = function (entree, options) {
      var promesse = fetchOriginal.apply(null, arguments);
      try {
        var url = typeof entree === 'string' ? entree : (entree && entree.url) || '';
        var methode = (options && options.method) || (entree && entree.method) || 'GET';
        if (String(methode).toUpperCase() === 'POST' && MUTATION.test(url)) {
          promesse.then(planifierReconciliation, function () {});
        }
      } catch (e) { /* observer ne doit jamais gêner */ }
      return promesse;
    };
  }

  reconcilier();
  // Gardé pour les thèmes qui, eux, diffusent l'événement. Celui d'Up'n Down ne
  // le fait pas — c'est l'observateur ci-dessus qui travaille.
  document.addEventListener('cart:updated', function () { reconcilier(); });
})();
